import { RootEpic, RootState } from 'MyTypes';
import { compact, isEmpty, omit, pick } from 'lodash/fp';
import { EMPTY, concat, defer, from, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';

import { TYPE_BAR_CHART, TYPE_LINE_CHART } from '@portal/common/models/chart-type';
import { getTimeUnitKeys } from '@portal/common/models/data-key';
import { AllDataTypes, getDataTypePrimaryEntityKey } from '@portal/common/models/data-type';
import { OPERATION_STATUS } from '@portal/common/models/operation-status';
import { isRequestCancelled } from '@portal/common/services/error-tracking';
import {
    AiChatDataCollectionOption,
    AiChatDataExplorerState,
    AiChatMessageResponse,
    ChartType,
    DataCollectionResource,
    DataFilters,
    DataGranularityKey,
    DataQueryParams,
    DataQueryType,
    DataType,
} from '@portal/common/types';

import api from '../../api';
import _ from '../../locale';
import { ANALYTICS_SOURCE_AI_CHAT } from '../../models/analytics-source';
import { getYearRefinementFilterMode } from '../../utility/data-explorer/initialization-plan';
import {
    dataFiltersToConditions,
    getDataCollectionDataTypes,
    getMergedDataCollectionGranularity,
} from '../../utility/data-loader';
import {
    applyDataExplorerInitializationPlan,
    changeSelectedDataType,
} from '../data-explorer/actions';
import { getDataCollections, getSelectedChartType } from '../data-explorer/selectors';
import { getOrganizationId } from '../organization/selectors';
import { updateLocation } from '../router/actions';
import { pushToast } from '../toasts/actions';
import { TOAST_KIND } from '../toasts/types';
import { changeDataCollection, initializeDataCollection } from '../user-settings/actions';
import {
    getSelectedDataCollectionForDataTool,
    getSelectedDataTool,
} from '../user-settings/selectors';
import {
    QueryResultRequest,
    addAssistantDataCollectionPicker,
    addAssistantFilters,
    addAssistantMessage,
    addAssistantQueryResult,
    cancelChatMessage,
    chatDataCollectionSwitchApplied,
    chatRequestFailed,
    fetchQueryResult,
    manualFiltersPosted,
    pickChatDataCollection,
    queryResultFailed,
    queryResultLoaded,
    resetChat,
    saveChatDataCollectionChoice,
    setActiveChat,
    setChatAutoDataCollection,
    setChatId,
    submitChatMessage,
    submitFeedback,
    submitFeedbackFailed,
    submitFeedbackSucceeded,
    switchChatDataCollection,
} from './actions';
import {
    getChatDataCollectionId,
    getChatId,
    getChatLastPickedDataCollectionId,
    getChatMessages,
    getChatRequestStatus,
    getIsChatAutoDataCollection,
    getManualFiltersToShare,
} from './selectors';
import { AiChatQueryResultMessage } from './types';
import {
    canMirrorQueryResultToDataExplorer,
    getAlternativeDataCollectionOptions,
    getChatErrorMessage,
    getLastFilterBearingMessage,
    getQueryResultErrorMessage,
    getRankedFilter,
    isAggregationResultMessage,
    isDataCollectionPickerMessage,
    isFeedbackableMessage,
    isQueryResultMessage,
    isRankingResultMessage,
    parseQueryResultPayload,
    pickNonEmptyParams,
    resolveAssistantDataTypes,
    resolveAssistantFilters,
    serializeDataTypes,
    toDataCollectionOptions,
} from './utils';

interface DataCollectionAnswer {
    responseId: string;
    queryType?: DataQueryType;
    params: DataQueryParams;
    visualization?: ChartType;
    option: AiChatDataCollectionOption;
    dataCollectionOptions: AiChatDataCollectionOption[];
}

const buildDataCollectionAnswerActions = ({
    responseId,
    queryType,
    params,
    visualization,
    option,
    dataCollectionOptions,
}: DataCollectionAnswer) => {
    const queryResult = parseQueryResultPayload({
        query_type: queryType,
        data_type: serializeDataTypes(option.dataTypes),
        filters: option.filters,
        params,
        visualization,
    });

    return [
        queryResult
            ? addAssistantQueryResult({
                  id: responseId,
                  ...queryResult,
                  dataCollectionId: option.id,
                  dataCollectionOptions,
              })
            : addAssistantFilters({
                  id: responseId,
                  dataTypes: option.dataTypes,
                  filters: option.filters,
                  visualization,
                  dataCollectionId: option.id,
              }),
        saveChatDataCollectionChoice({ responseId, dataCollectionId: option.id }),
    ];
};

interface ResponseContext {
    currentChatId: string | null;
    dataCollectionId: number | null;
    lastPickedDataCollectionId: number | null;
    prompt: string;
}

const buildResponseActions = (
    data: AiChatMessageResponse,
    { currentChatId, dataCollectionId, lastPickedDataCollectionId, prompt }: ResponseContext
) => {
    const actions: Array<
        | ReturnType<typeof setChatId>
        | ReturnType<typeof addAssistantMessage>
        | ReturnType<typeof addAssistantFilters>
        | ReturnType<typeof addAssistantQueryResult>
        | ReturnType<typeof addAssistantDataCollectionPicker>
        | ReturnType<typeof saveChatDataCollectionChoice>
        | ReturnType<typeof chatRequestFailed>
    > = [];
    const {
        chat_id,
        success,
        error,
        applied_filters: appliedFilters,
        applied_data_type: appliedDataType,
        resolved_data_collections: suggestedDataCollections,
    } = data;
    const id = data.response_id ?? uuidv4();
    const dataCollections = success?.resolved_data_collections;

    if (chat_id && chat_id !== currentChatId) {
        actions.push(setChatId(chat_id));
    }
    // `success` = a parsed query (filter panel, or inline DataResponse for ranking/aggregation);
    // `error` = AI clarification, or with suggested collections, no data in the selected one;
    // else malformed.
    if (success && !isEmpty(dataCollections)) {
        const options = toDataCollectionOptions(
            dataCollections,
            resolveAssistantDataTypes(success, appliedDataType)
        );
        const [onlyOption] = options;
        const settledOption =
            options.length === 1
                ? onlyOption
                : options.find(({ id: optionId }) => optionId === lastPickedDataCollectionId);

        if (settledOption) {
            actions.push(
                ...buildDataCollectionAnswerActions({
                    responseId: id,
                    queryType: success.query_type,
                    params: pickNonEmptyParams(success.params),
                    visualization: success.visualization,
                    option: settledOption,
                    dataCollectionOptions: options,
                })
            );
        } else {
            actions.push(
                addAssistantDataCollectionPicker({
                    responseId: id,
                    content: error ?? null,
                    options,
                    queryType: success.query_type,
                    params: pickNonEmptyParams(success.params),
                    visualization: success.visualization,
                })
            );
        }
    } else if (success) {
        const queryResult = parseQueryResultPayload(success, appliedFilters, appliedDataType);
        if (queryResult) {
            actions.push(
                addAssistantQueryResult({
                    id,
                    ...queryResult,
                    dataCollectionId,
                    dataCollectionOptions: [],
                })
            );
        } else {
            actions.push(
                addAssistantFilters({
                    id,
                    // Prefer server-applied filters, matching the query-result/history paths.
                    dataTypes: resolveAssistantDataTypes(success, appliedDataType),
                    filters: resolveAssistantFilters(success, appliedFilters),
                    visualization: success.visualization,
                    dataCollectionId,
                })
            );
        }
    } else if (error && !isEmpty(suggestedDataCollections)) {
        actions.push(
            addAssistantDataCollectionPicker({
                responseId: id,
                content: error,
                options: toDataCollectionOptions(suggestedDataCollections, []),
                params: {},
            })
        );
    } else if (error) {
        actions.push(addAssistantMessage({ id, content: error, dataCollectionId }));
    } else {
        actions.push(
            chatRequestFailed({
                id: uuidv4(),
                content: _('dex_chat_error_default'),
                failedInput: prompt,
            })
        );
    }
    return actions;
};

const postManualFilters$ = ({
    organizationId,
    chatId,
    promptId,
    manualFilters,
    signal,
}: {
    organizationId: number;
    chatId: string;
    promptId: string;
    manualFilters: AiChatDataExplorerState;
    signal: AbortSignal;
}) =>
    defer(() =>
        api.aiChat.postManualFilters({ organizationId, chatId, ...manualFilters, signal })
    ).pipe(
        // Null when nothing was stored.
        mergeMap((response) =>
            response != null
                ? of(manualFiltersPosted({ id: uuidv4(), promptId, manualFilters }))
                : EMPTY
        ),
        catchError((error) =>
            isRequestCancelled(error)
                ? EMPTY
                : of(
                      pushToast({
                          id: uuidv4(),
                          kind: TOAST_KIND.ERROR,
                          message: _('dex_chat_manual_filters_share_error'),
                      })
                  )
        )
    );

export const submitChatMessageEpic: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(submitChatMessage)),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const organizationId = getOrganizationId(state);
            const isAutoDataCollection = getIsChatAutoDataCollection(state);
            const dataCollectionId = isAutoDataCollection
                ? null
                : (getSelectedDataCollectionForDataTool(state)?.id ?? null);
            const lastPickedDataCollectionId = isAutoDataCollection
                ? getChatLastPickedDataCollectionId(state)
                : null;
            const chatId = getChatId(state);

            if (!organizationId || (!isAutoDataCollection && dataCollectionId == null)) {
                return of(
                    chatRequestFailed({
                        id: uuidv4(),
                        content: _('dex_chat_error_default'),
                        failedInput: action.payload.content,
                    })
                );
            }

            const controller = new AbortController();
            const manualFilters = getManualFiltersToShare(state);

            const manualFilters$ =
                chatId && manualFilters
                    ? postManualFilters$({
                          organizationId,
                          chatId,
                          promptId: action.payload.id,
                          manualFilters,
                          signal: controller.signal,
                      })
                    : EMPTY;

            const prompt$ = defer(() =>
                api.aiChat.submitMessage({
                    organizationId,
                    chatId,
                    prompt: action.payload.content,
                    dataCollectionId,
                    signal: controller.signal,
                })
            ).pipe(
                mergeMap((data) =>
                    // Filters are normalized downstream in buildResponseActions.
                    from(
                        buildResponseActions(data, {
                            currentChatId: chatId,
                            dataCollectionId,
                            lastPickedDataCollectionId,
                            prompt: action.payload.content,
                        })
                    )
                ),
                catchError((error) =>
                    isRequestCancelled(error)
                        ? EMPTY
                        : of(
                              chatRequestFailed({
                                  id: uuidv4(),
                                  content:
                                      getChatErrorMessage(error) ?? _('dex_chat_error_default'),
                                  failedInput: action.payload.content,
                              })
                          )
                )
            );

            return concat(manualFilters$, prompt$).pipe(
                takeUntil(
                    action$.pipe(filter(isActionOf([cancelChatMessage, resetChat, setActiveChat])))
                ),
                finalize(() => controller.abort())
            );
        })
    );

export const submitFeedbackEpic: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(submitFeedback)),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const organizationId = getOrganizationId(state);
            const chatId = getChatId(state);
            const { responseId, reaction, categories, comment } = action.payload;

            if (!organizationId || !chatId) {
                return of(
                    submitFeedbackFailed({
                        errorMessage: _('dex_chat_feedback_error_missing_context'),
                    })
                );
            }

            const trimmed = comment.trim();
            const commentPayload = trimmed.length > 0 ? trimmed : null;
            const categoriesPayload = categories.length > 0 ? categories : null;

            return from(
                api.aiChat.submitFeedback({
                    organizationId,
                    chatId,
                    responseId,
                    reaction,
                    comment: commentPayload,
                    categories: categoriesPayload,
                })
            ).pipe(
                map(() => submitFeedbackSucceeded({ responseId, reaction })),
                catchError((error) =>
                    of(
                        submitFeedbackFailed({
                            errorMessage:
                                getChatErrorMessage(error) ?? _('dex_chat_feedback_error_default'),
                        })
                    )
                )
            );
        })
    );

const buildDataExplorerInitializationPlanAction = ({
    state,
    dataCollectionId,
    dataTypes,
    filters,
    chartType,
    dataTool,
}: {
    state: RootState;
    dataCollectionId: number;
    dataTypes: DataType[];
    filters: DataFilters;
    chartType: ChartType;
    dataTool: string;
}): ReturnType<typeof applyDataExplorerInitializationPlan> => {
    const primaryEntityKeys = compact(dataTypes.map(getDataTypePrimaryEntityKey));
    const conditions = dataFiltersToConditions(pick(primaryEntityKeys, filters)).filter(
        ({ data_type }) => dataTypes.includes(data_type)
    );
    const refinementFilters = omit(primaryEntityKeys, filters);
    const dataCollection = (getDataCollections(state) ?? []).find(
        (collection) => collection.id === dataCollectionId
    );
    const availableDataTypes = dataCollection ? getDataCollectionDataTypes(dataCollection) : [];

    return applyDataExplorerInitializationPlan({
        dataCollectionId,
        dataTool,
        dataType:
            dataTypes.length > 1 && availableDataTypes.includes(AllDataTypes)
                ? AllDataTypes
                : dataTypes[0],
        chartType,
        conditions,
        refinementFilters,
        yearRefinementFilterMode: getYearRefinementFilterMode(refinementFilters),
        shouldQuery: true,
        querySource: ANALYTICS_SOURCE_AI_CHAT,
    });
};

export const runQueryOnAssistantFilters: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(addAssistantFilters)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const dataCollectionId =
                action.payload.dataCollectionId ?? getSelectedDataCollectionForDataTool(state)?.id;

            if (dataCollectionId == null) {
                return EMPTY;
            }

            const { dataTypes, filters, visualization } = action.payload;
            const chartType = visualization ?? getSelectedChartType(state);
            const dataTool = getSelectedDataTool(state);

            return of(
                buildDataExplorerInitializationPlanAction({
                    state,
                    dataTypes,
                    filters,
                    dataCollectionId,
                    chartType,
                    dataTool,
                })
            );
        })
    );

const withFullTimeUnitRange = (
    filters: DataFilters,
    dataTypes: DataType[],
    dataCollection: DataCollectionResource | undefined
): DataFilters => {
    const granularity = getMergedDataCollectionGranularity(dataCollection, dataTypes);

    return getTimeUnitKeys().reduce<DataFilters>((widened, timeUnitKey) => {
        const key = timeUnitKey as DataGranularityKey;
        const availableRange = granularity[key];

        if (isEmpty(widened[key])) {
            return widened;
        }

        return isEmpty(availableRange)
            ? omit([key], widened)
            : { ...widened, [key]: availableRange };
    }, filters);
};

const resolveMessageDataCollectionId = (
    message: AiChatQueryResultMessage,
    state: RootState
): number | undefined =>
    message.dataCollectionId ??
    getChatDataCollectionId(state) ??
    getSelectedDataCollectionForDataTool(state)?.id;

// Overlaid after the widening, so a ranking over a time unit keeps its own result.
const buildRankingMirrorFilters = (
    message: AiChatQueryResultMessage,
    dataCollection: DataCollectionResource | undefined
): DataFilters => ({
    ...withFullTimeUnitRange(message.filters, message.dataTypes, dataCollection),
    ...getRankedFilter(message),
});

const buildQueryResultMirrorFilters = (
    message: AiChatQueryResultMessage,
    dataCollection: DataCollectionResource | undefined
): DataFilters =>
    isRankingResultMessage(message)
        ? buildRankingMirrorFilters(message, dataCollection)
        : message.filters;

const getQueryResultMirrorChartType = (
    message: AiChatQueryResultMessage,
    selectedChartType: ChartType
): ChartType => {
    const { filters, visualization } = message;
    if (visualization) {
        return visualization;
    }

    if (isRankingResultMessage(message)) {
        return TYPE_LINE_CHART;
    }

    if (isAggregationResultMessage(message)) {
        const hasMultipleTimeUnitValues = getTimeUnitKeys().some(
            (key) => (filters[key]?.length ?? 0) > 1
        );

        return hasMultipleTimeUnitValues ? TYPE_LINE_CHART : TYPE_BAR_CHART;
    }

    return selectedChartType;
};

export const runQueryOnAssistantQueryResult: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(queryResultLoaded)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const message = getLastFilterBearingMessage(getChatMessages(state));

            if (
                !message ||
                message.id !== action.payload.id ||
                !canMirrorQueryResultToDataExplorer(message)
            ) {
                return EMPTY;
            }

            const dataCollectionId = resolveMessageDataCollectionId(message, state);

            if (dataCollectionId == null) {
                return EMPTY;
            }

            const dataCollection = (getDataCollections(state) ?? []).find(
                (collection) => collection.id === dataCollectionId
            );

            return of(
                buildDataExplorerInitializationPlanAction({
                    state,
                    dataTypes: message.dataTypes,
                    filters: buildQueryResultMirrorFilters(message, dataCollection),
                    dataCollectionId,
                    chartType: getQueryResultMirrorChartType(message, getSelectedChartType(state)),
                    dataTool: getSelectedDataTool(state),
                })
            );
        })
    );

export const cancelChatMessageOnDataCollectionChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([changeDataCollection, setChatAutoDataCollection])),
        withLatestFrom(state$),
        filter(([, state]) => getChatRequestStatus(state) === OPERATION_STATUS.PENDING),
        map(() => cancelChatMessage())
    );

export const applyPickedDataCollection: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(pickChatDataCollection)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const { messageId, dataCollectionId } = action.payload;
            const message = getChatMessages(state).find(({ id }) => id === messageId);

            if (!message || !isDataCollectionPickerMessage(message)) {
                return EMPTY;
            }

            const option = message.options.find(({ id }) => id === dataCollectionId);

            if (!option) {
                return EMPTY;
            }

            const { responseId, queryType, params, visualization, options } = message;

            // The answer's own init plan moves Data Explorer to the picked collection.
            return from(
                buildDataCollectionAnswerActions({
                    responseId,
                    queryType,
                    params,
                    visualization,
                    option,
                    dataCollectionOptions: options,
                })
            );
        })
    );

export const applySwitchedDataCollection: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(switchChatDataCollection)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const { responseId, dataCollectionId } = action.payload;
            const message = getChatMessages(state).find(({ id }) => id === responseId);

            if (!message || !isQueryResultMessage(message)) {
                return EMPTY;
            }

            const option = getAlternativeDataCollectionOptions(message).find(
                ({ id }) => id === dataCollectionId
            );

            if (!option) {
                return EMPTY;
            }

            const { queryType, params } = message;
            const { dataTypes, filters } = option;

            return of(
                chatDataCollectionSwitchApplied({
                    responseId,
                    dataCollectionId,
                    dataTypes,
                    filters,
                }),
                fetchQueryResult({
                    id: responseId,
                    queryType,
                    dataType: dataTypes[0],
                    filters,
                    params,
                    dataCollectionId,
                }),
                saveChatDataCollectionChoice({ responseId, dataCollectionId })
            );
        })
    );

export const saveDataCollectionChoice: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(saveChatDataCollectionChoice)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const organizationId = getOrganizationId(state);
            const chatId = getChatId(state);

            if (!organizationId || !chatId) {
                return EMPTY;
            }

            return from(
                api.aiChat.saveDataCollectionChoice({
                    organizationId,
                    chatId,
                    responseId: action.payload.responseId,
                    dataCollectionId: action.payload.dataCollectionId,
                })
            ).pipe(
                mergeMap(() => EMPTY),
                catchError(() => EMPTY)
            );
        })
    );

export const resetChatOnDataTypeChange: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf([changeDataCollection, changeSelectedDataType])),
        startWith(null),
        pairwise(),
        // A data collection change retypes the data tool on its own, and that must not reset the
        // chat; only a data type change the user made on its own does.
        filter(
            ([previous, current]) =>
                isActionOf(changeSelectedDataType)(current) &&
                (previous == null || !isActionOf(changeDataCollection)(previous))
        ),
        map(() => resetChat())
    );

export const resetChatOnPageChange: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf(updateLocation)),
        map((action) => action.payload.pathname),
        distinctUntilChanged(),
        map(() => resetChat())
    );

export const applyLatestFiltersOnChatLoad: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(setActiveChat)),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const latestFilterMessage = getLastFilterBearingMessage(action.payload.messages);
            const dataTool = getSelectedDataTool(state);

            if (!latestFilterMessage || isQueryResultMessage(latestFilterMessage)) {
                if (action.payload.dataCollectionId != null) {
                    return of(
                        initializeDataCollection({
                            dataCollectionId: action.payload.dataCollectionId,
                            dataTool,
                        })
                    );
                }
                return EMPTY;
            }

            // Prefer the chat's own collection; fall back to the selected one for older
            // chats that predate the field.
            const dataCollectionId =
                (isFeedbackableMessage(latestFilterMessage)
                    ? latestFilterMessage.dataCollectionId
                    : null) ??
                action.payload.dataCollectionId ??
                getSelectedDataCollectionForDataTool(state)?.id ??
                null;

            if (dataCollectionId == null) {
                return EMPTY;
            }

            return of(
                buildDataExplorerInitializationPlanAction({
                    state,
                    dataTypes: latestFilterMessage.dataTypes,
                    filters: latestFilterMessage.filters,
                    dataCollectionId,
                    chartType: latestFilterMessage.visualization ?? getSelectedChartType(state),
                    dataTool,
                })
            );
        })
    );

// Shared fetch for any data query type: hits the generic {dataType}/{queryType} endpoint.
const fetchQueryResultData$ = (payload: QueryResultRequest) => {
    const { id, dataType, queryType, filters, params, dataCollectionId } = payload;

    if (dataCollectionId == null) {
        return of(queryResultFailed({ id, error: _('dex_chat_error_default') }));
    }

    // `defer` so a synchronous throw while building the request surfaces as a caught
    // stream error rather than tearing down the epic.
    return defer(() =>
        api.data.queryDataCollectionByQueryType(
            dataCollectionId,
            dataType,
            queryType,
            filters,
            params
        )
    ).pipe(
        map((response) => queryResultLoaded({ id, data: response })),
        catchError((error) =>
            of(
                queryResultFailed({
                    id,
                    error: getQueryResultErrorMessage(error),
                })
            )
        )
    );
};

// Live flow: a freshly added query-result message triggers its data fetch.
export const fetchQueryResultOnAdd: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf(addAssistantQueryResult)),
        map(({ payload }) => fetchQueryResult({ ...payload, dataType: payload.dataTypes[0] }))
    );

export const fetchQueryResultEpic: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf(fetchQueryResult)),
        mergeMap((action) => fetchQueryResultData$(action.payload))
    );

// History flow: re-fetch data for query-result messages restored from a saved chat.
export const fetchQueryResultsOnChatLoad: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf(setActiveChat)),
        mergeMap((action) =>
            from(
                action.payload.messages
                    .filter(isQueryResultMessage)
                    .filter((message) => message.data == null)
                    .map((message) =>
                        fetchQueryResult({
                            id: message.id,
                            queryType: message.queryType,
                            dataType: message.dataTypes[0],
                            filters: message.filters,
                            params: message.params,
                            dataCollectionId: message.dataCollectionId,
                        })
                    )
            )
        )
    );
